import type ConstructionListItem from "../../../types/ConstructionListItem";
import type ConstructionBulkEdit from "../../../types/ConstructionBulkEdit";
import type ApiResponse from "../../../../../services/api/types/ApiResponse";
import type ConstructionLocationCodes from "../../../types/ConstructionLocationCodes";
import {type IComboBox, type IComboBoxOption } from "@fluentui/react";
import {type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useConstructionNewHook from "../../../../../hooks/useConstructionNewHook";
import {type RootState } from "../../../../../store";
import { loadLocationData, updateEntities } from "../../../constructionsNewSlice";

interface UseEditConstructionsNewDialogHookProps {
    onDismiss: () => void;
    selectedConstructions: ConstructionListItem[];
}

interface UseEditConstructionDialogHookReturn {
  loading: boolean;
  error: string | undefined;
  formData: ConstructionBulkEdit;
  constructionLocationCodes: IComboBoxOption[] | undefined;
  constructionLocationTypes: IComboBoxOption[] | undefined;
  handleComboBoxChange: (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption,
    index?: number,
    value?: string,
  ) => void;
  handleDismiss: () => void;
  handleConfirm: (
    event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
  ) => void;
  handleNumericFieldChange: (
    event: React.SyntheticEvent<HTMLElement>,
    newValue?: string,
  ) => void;
}


const useEditConstructionsNewDialogHook = ({
    onDismiss,
    selectedConstructions,
}: UseEditConstructionsNewDialogHookProps): UseEditConstructionDialogHookReturn => {
    const selectedIds = selectedConstructions.map(c => c.id);
    const { t } = useTranslation(["common"]);
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const [error, setError] = useState<string>();
    const initialFormData = useMemo(() => {
      if (selectedConstructions.length === 1) {
        const construction = selectedConstructions[0];
        return {
          ids: selectedIds,
          phase: construction.phase,
          locationCode: construction.locationCode,
          locationType: construction.locationType,
        };
      }
      return {
        ids: selectedIds,
        phase: undefined,
        locationCode: undefined,
        locationType: undefined,
      };
    }, [selectedConstructions, selectedIds]);
    const [formData, setFormData] = useState<ConstructionBulkEdit>(initialFormData);

    const { getLocationCodes, getLocationTypes, bulkEdit } = useConstructionNewHook();

    const { constructionLocationCodes, constructionLocationTypes } = useSelector((state: RootState) => state.constructionsNew);

    const handleNumericFieldChange = (
      event: React.SyntheticEvent<HTMLElement>,
      value?: string,
    ): void => {
      setFormData(prevState => ({
        ...prevState,
        phase: Number(value),
      }));
    };

    const handleComboBoxChange = (
      event: React.FormEvent<IComboBox>,
      option?: IComboBoxOption,
      index?: number,
      value?: string,
    ): void => {
      const comboBoxElement = event.currentTarget as unknown as HTMLElement;
      const id = comboBoxElement.id.substring(
        0,
        comboBoxElement.id.indexOf("-"),
      );

      setFormData(prevState => ({
        ...prevState,
        [id]: value,
      }));
    };

    const handleDismiss = (): void => {
      setFormData(prevState => ({
        ...prevState,
        locationCode: undefined,
        phase: undefined,
        locationType: undefined,
      }));
      onDismiss();
    };

    const handleConfirm = (
      event: SyntheticEvent<HTMLFormElement, SubmitEvent>,
    ): void => {
      event.preventDefault();

      void (async () => {
        if (
          !formData.locationCode ||
          !formData.locationType ||
          !formData.phase
        ) {
          setError(t("requiredFields"));
          return;
        }

        handleDismiss();

        const payload = { ...formData, ids: selectedIds };

        await bulkEdit(payload);
        dispatch(updateEntities(payload));
      })();
    };

    useEffect(() => {
      void (async () => {
        const locationCodesResponse: ApiResponse<ConstructionLocationCodes[]> = await getLocationCodes();
        const locationTypesResponse: ApiResponse<string[]> = await getLocationTypes();

        if (
          locationCodesResponse.success &&
          locationCodesResponse.data &&
          locationTypesResponse.success &&
          locationTypesResponse.data
        ) {
          dispatch(
            loadLocationData({
              locationCodes: locationCodesResponse.data,
              locationTypes: locationTypesResponse.data,
            }),
          );
        } else {
          setError(
            locationCodesResponse.errors ??
              locationTypesResponse.errors,
          );
        }

        setLoading(false);
      })();
    }, [dispatch, getLocationCodes, getLocationTypes]);

    return {
      loading,
      error,
      formData,
      constructionLocationCodes,
      constructionLocationTypes,
      handleConfirm,
      handleDismiss,
      handleComboBoxChange,
      handleNumericFieldChange,
    };
};

export default useEditConstructionsNewDialogHook;